/**
 * Case management shapes.
 *
 * A case is an investigation: the bookmarks it files, the narrative around
 * them, and the evidence that is not video. These are the fields the cases
 * API emits, with its RFC 3339 times read into milliseconds at the transport
 * (api/transformers/cases.ts). The requirements are in
 * docs/superpowers/specs/2026-09-19-case-management-ui-requirements.md.
 *
 * Frozen objects rather than enums for the closed sets: the values arrive as
 * parsed JSON, and an enum member is not assignable from a string without a
 * cast at every entry point.
 */

import { z } from 'zod';

import type { Bookmark, BookmarkCardActions } from './bookmarks';
import {
    Case as CaseDtoSchema,
    CaseAttachment as CaseAttachmentDtoSchema,
    CaseDetail as CaseDetailDtoSchema,
    GetCaseAttachmentContentResponse as CaseAttachmentContentDtoSchema,
} from './generated/vms_database.zod';
import type { Write } from './writes';

export const CaseStatus = {
    Open: 'open',
    Review: 'review',
    Closed: 'closed',
} as const;

export type CaseStatus = (typeof CaseStatus)[keyof typeof CaseStatus];

/** The files an operator brings to a case; the archive it holds is bookmarks. */
export const CaseAttachmentKind = {
    Image: 'image',
    Video: 'video',
    Document: 'document',
} as const;

export type CaseAttachmentKind = (typeof CaseAttachmentKind)[keyof typeof CaseAttachmentKind];

export const CaseStatusSchema = z.enum(CaseStatus);
export const CaseAttachmentKindSchema = z.enum(CaseAttachmentKind);

/** The most bytes one attachment may hold: the server's own cap on an upload. */
export const MAX_CASE_ATTACHMENT_BYTES = 100 * 1024 * 1024;

export { CaseAttachmentDtoSchema };
export type CaseAttachmentDto = z.infer<typeof CaseAttachmentDtoSchema>;

/** A file filed into a case. Its bytes are read through a URL signed for the reader. */
export interface CaseAttachment {
    id: string;
    kind: CaseAttachmentKind;
    /** The title, which the operator may rewrite. */
    name: string;
    /** The upload's own name, which a download is saved as. */
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    /** The operator's own words about this piece of evidence, as Markdown. */
    note: string;
    addedBy: string;
    addedAtMs: number;
    updatedAtMs: number;
}

/** The case alone, as a write answers it. */
export { CaseDtoSchema };
export type CaseDto = z.infer<typeof CaseDtoSchema>;

/** A listed case: the case with what is filed into it. */
export { CaseDetailDtoSchema };
export type CaseDetailDto = z.infer<typeof CaseDetailDtoSchema>;

export interface Case {
    id: string;
    /**
     * The number an operator quotes, CASE-2026-09-30-01: the date the case was
     * opened, then its place among that date's cases.
     */
    number: string;
    title: string;
    /** The narrative, as Markdown. */
    description: string;
    status: CaseStatus;
    ownerEmail: string;
    createdAtMs: number;
    updatedAtMs: number;
}

export interface CaseDetail extends Case {
    /** Ordered by the operator: a case is a narrative, and the order is part of it. */
    attachments: CaseAttachment[];
    /** The bookmarks filed into the case, earliest span first once the cases store has them. */
    bookmarks: Bookmark[];
}

/** What a case's own fields may be changed to. */
export type CasePatch = Partial<Pick<Case, 'title' | 'description' | 'status'>>;

/** What an attachment may be changed to; `position` is its new place in the case's order. */
export interface CaseAttachmentPatch {
    name?: string;
    note?: string;
    position?: number;
}

export { CaseAttachmentContentDtoSchema };
export type CaseAttachmentContentDto = z.infer<typeof CaseAttachmentContentDtoSchema>;

/** Where an attachment's bytes are read from, and until when the URL is good. */
export interface CaseAttachmentContent {
    url: string;
    expiresAtMs: number;
}

/** The URL a reader of an attachment holds, as the browser keeps it between sign requests. */
export interface CaseAttachmentUrl {
    /** Null until a URL arrives, and once the last one expired with no other signed. */
    url: string | null;
    /** When `url` stops being good; 0 while there is none. */
    expiresAtMs: number;
    /** Why the last signing failed; a new one clears it. */
    error: Error | null;
}

/** A case a bookmark is being filed into; `record` is null for one not yet started. */
export interface CaseFiling {
    record: CaseDetail | null;
}

/** The bookmark as a filing needs it: what to file, and what to call a case started for it. */
export type FiledBookmark = Pick<Bookmark, 'id' | 'deviceId' | 'startTimeMs'> &
    Partial<Pick<Bookmark, 'description'>>;

/**
 * What the case panel does to the bookmarks filed into a case. The panel makes
 * the calls; the sections offering them take them from it. Each settles once
 * the write is done, having said itself what went wrong.
 */
export interface CaseBookmarkActions extends BookmarkCardActions {
    /** The case-wide switch: every bookmark named, reported as one change. */
    setProtectedAll: (bookmarks: readonly Bookmark[], shouldProtect: boolean) => Promise<void>;
    /** Takes the bookmark out of the case; the bookmark stays, filed nowhere. */
    removeFromCase: (bookmark: Bookmark) => Promise<void>;
}

/**
 * What the case panel does to the case itself. The panel makes the calls; the sections offering
 * them take them from it. Each settles once the write is done, having said itself what went
 * wrong.
 */
export interface CaseActions {
    rename: (title: string) => Promise<void>;
    setStatus: (status: CaseStatus) => Promise<void>;
    describe: (description: string) => Promise<void>;
    /** Uploads one file to the end of the case's attachments. */
    attach: (file: File) => Promise<void>;
    setAttachmentNote: (attachmentId: string, note: string) => Promise<void>;
    /** Moves an attachment one place along the case's order, which is the order shown. */
    moveAttachment: (attachmentId: string, delta: -1 | 1) => Promise<void>;
    removeAttachment: (attachmentId: string) => Promise<void>;
    retryWrite: (write: CaseWrite) => void;
    discardWrite: (write: CaseWrite) => void;
}

/** The four faces of a case, in the order the case-mode tab strip shows them. */
export enum CaseTab {
    Videos = 'videos',
    Description = 'description',
    Attachments = 'attachments',
    Settings = 'settings',
}

/** What every reader of the cases sees (state/api/casesAtoms.ts). */
export interface CasesSnapshot {
    cases: CaseDetail[];
    /** A read is in flight: the first, or one after a write. */
    isLoading: boolean;
    /** True once a read of the cases has gone through. */
    isLoaded: boolean;
    /** Why the last read failed; null while it did not. */
    error: Error | null;
}

export enum CaseWriteKind {
    Create = 'create',
    Update = 'update',
    AttachmentUpdate = 'attachmentUpdate',
}

/** What a write of this session sends to the server. */
export type CaseWriteRequest =
    | { kind: CaseWriteKind.Create; title: string } |
    { kind: CaseWriteKind.Update; caseId: string; patch: CasePatch } |
    {
        kind: CaseWriteKind.AttachmentUpdate;
        caseId: string;
        attachmentId: string;
        patch: CaseAttachmentPatch;
    };

export type CaseWrite = Write<CaseWriteRequest>;

/** A new case that the list does not show yet. */
export interface PendingCase {
    title: string;
    createWrite: CaseWrite;
}
